/*
 * Super Admin — Terms & Conditions Management (Phase 1).
 *
 * Mounted at /app/terms. Edits are database-driven via /api/admin/terms.
 * Workflow:
 *   • Browse all versions for a product (default: caporilli_diagnostic)
 *   • Create new draft version (paste body text)
 *   • Edit drafts only (published+active locked — must create new version)
 *   • Publish a draft → auto-archives the prior active version
 *   • View acceptance audit trail per version
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  FileText, Plus, Eye, CheckCircle2, Archive, Save, ChevronRight, ShieldCheck, Pencil, Users,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const adminHdrs = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id":   session?.user?.id   || "",
  "x-user-role": session?.user?.role || "admin",
});

const STATUS_PILL = {
  draft:     "text-[#a16207] bg-[#fefce8] border-[#fde047]",
  published: "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]",
  archived:  "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]",
};

const PRODUCT_OPTIONS = [
  { id: "caporilli_diagnostic", label: "MySchoolRx" },
];

const TermsAdminPage = ({ session }) => {
  const [productType, setProductType] = useState("caporilli_diagnostic");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [acceptances, setAcceptances] = useState([]);
  const [showAcceptances, setShowAcceptances] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/terms?product_type=${productType}`, { headers: adminHdrs(session) });
      setDocs(r.data || []);
      if (r.data?.length && !selected) setSelected(r.data[0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load terms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productType]);

  const active = useMemo(() => docs.find((d) => d.is_active && d.status === "published"), [docs]);

  const saveDraft = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/api/admin/terms/${id}`, patch, { headers: adminHdrs(session) });
      setDocs((arr) => arr.map((d) => (d.id === id ? r.data : d)));
      setSelected(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const publish = async (id) => {
    if (!window.confirm("Publish this version? The currently active version will be archived.")) return;
    try {
      const r = await axios.post(`${API}/api/admin/terms/${id}/publish`, {}, { headers: adminHdrs(session) });
      toast.success(`Published version ${r.data.version}`);
      await load();
      setSelected(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this Terms version?")) return;
    try {
      await axios.post(`${API}/api/admin/terms/${id}/archive`, {}, { headers: adminHdrs(session) });
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const viewAcceptances = async (doc) => {
    try {
      const r = await axios.get(`${API}/api/admin/terms/${doc.id}/acceptances`, { headers: adminHdrs(session) });
      setAcceptances(r.data || []);
      setShowAcceptances(true);
    } catch (e) {
      toast.error("Failed to load acceptances");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto" data-testid="terms-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Operations</div>
          <h1 className="text-3xl font-bold text-[#111827] flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-[#3B4FE4]" /> Terms & Conditions Management
          </h1>
          <p className="text-sm text-[#6b7280] mt-1">
            Database-driven, versioned legal terms. Paste updated language any time — old versions stay viewable for audit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={productType}
            onChange={(e) => { setProductType(e.target.value); setSelected(null); }}
            className="border border-[#e5e7eb] bg-white text-[#111827] text-[13px] rounded-md px-3 py-2"
            data-testid="terms-product-select"
          >
            {PRODUCT_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <Button
            onClick={() => setShowNew(true)}
            className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white"
            data-testid="terms-new-version-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New Version
          </Button>
        </div>
      </div>

      {/* Active banner */}
      {active && (
        <div className="rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] px-4 py-3 mb-5 flex items-center gap-3" data-testid="terms-active-banner">
          <CheckCircle2 className="h-5 w-5 text-[#059669] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-[#065f46]">
              <strong>Active:</strong> {active.title} · v{active.version} · effective {new Date(active.effective_date).toLocaleDateString()}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => viewAcceptances(active)} className="border-[#a7f3d0] text-[#065f46]" data-testid="terms-view-acceptances">
            <Users className="h-3.5 w-3.5 mr-1" /> Acceptances
          </Button>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-[#6b7280]">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[340px_1fr] gap-6">
          {/* Versions list */}
          <aside className="lg:sticky lg:top-6 lg:self-start" data-testid="terms-version-list">
            <div className="rounded-xl border border-[#e5e7eb] bg-white p-2 space-y-1">
              {docs.length === 0 && (
                <div className="px-3 py-6 text-center text-[#9ca3af] text-[13px]">No versions yet</div>
              )}
              {docs.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setSelected(d)}
                  className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${
                    selected?.id === d.id ? "bg-[#0b1220] text-white" : "hover:bg-[#f9fafb]"
                  }`}
                  data-testid={`terms-version-${d.version}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className={`text-[13px] font-semibold truncate ${selected?.id === d.id ? "text-white" : "text-[#111827]"}`}>
                        v{d.version} — {d.title}
                      </div>
                      <div className={`text-[11.5px] mt-0.5 ${selected?.id === d.id ? "text-white/60" : "text-[#6b7280]"}`}>
                        {new Date(d.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${STATUS_PILL[d.status]}`}>
                      {d.status}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Document editor / viewer */}
          <section>
            {!selected ? (
              <div className="rounded-xl border border-[#e5e7eb] bg-white p-12 text-center text-[#9ca3af]">
                Select a version to view or edit
              </div>
            ) : (
              <TermsDocumentEditor
                key={selected.id}
                doc={selected}
                onSave={saveDraft}
                onPublish={publish}
                onArchive={archive}
                onViewAcceptances={() => viewAcceptances(selected)}
              />
            )}
          </section>
        </div>
      )}

      {/* New version modal */}
      {showNew && (
        <NewTermsModal
          productType={productType}
          session={session}
          onClose={() => setShowNew(false)}
          onCreated={(doc) => { setShowNew(false); load(); setSelected(doc); }}
        />
      )}

      {/* Acceptances drawer */}
      {showAcceptances && (
        <AcceptancesDrawer
          acceptances={acceptances}
          onClose={() => setShowAcceptances(false)}
        />
      )}
    </div>
  );
};

const TermsDocumentEditor = ({ doc, onSave, onPublish, onArchive, onViewAcceptances }) => {
  const [title, setTitle] = useState(doc.title);
  const [version, setVersion] = useState(doc.version);
  const [body, setBody] = useState(doc.body);
  const [effectiveDate, setEffectiveDate] = useState((doc.effective_date || "").substring(0, 10));
  const [notes, setNotes] = useState(doc.notes || "");
  const [preview, setPreview] = useState(false);

  const editable = doc.status === "draft";
  const dirty = title !== doc.title || version !== doc.version || body !== doc.body || notes !== (doc.notes || "");

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white p-6" data-testid="terms-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">
            {doc.status === "draft" ? "Draft" : doc.status === "published" ? (doc.is_active ? "Published — Active" : "Published") : "Archived"}
          </div>
          <h2 className="text-xl font-bold text-[#111827]">v{doc.version} · {doc.title}</h2>
          <div className="text-[11.5px] text-[#6b7280] mt-0.5">
            Effective {new Date(doc.effective_date).toLocaleDateString()} · Last updated {new Date(doc.updated_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPreview((p) => !p)} className="border-[#e5e7eb]" data-testid="terms-preview-toggle">
            <Eye className="h-3.5 w-3.5 mr-1" /> {preview ? "Edit" : "Preview"}
          </Button>
          {doc.status === "draft" && (
            <Button size="sm" onClick={() => onPublish(doc.id)} className="bg-[#059669] hover:bg-[#047857] text-white" data-testid="terms-publish-btn">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Publish
            </Button>
          )}
          {doc.status === "published" && !doc.is_active && (
            <Button size="sm" variant="outline" onClick={() => onArchive(doc.id)} className="border-[#e5e7eb] text-[#6b7280]" data-testid="terms-archive-btn">
              <Archive className="h-3.5 w-3.5 mr-1" /> Archive
            </Button>
          )}
          {doc.status === "published" && (
            <Button size="sm" variant="outline" onClick={onViewAcceptances} className="border-[#e5e7eb]" data-testid="terms-editor-view-acceptances">
              <Users className="h-3.5 w-3.5 mr-1" /> Acceptances
            </Button>
          )}
        </div>
      </div>

      {!editable && (
        <div className="rounded-md border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2 text-[12px] text-[#1e3a8a] mb-4 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
          {doc.is_active
            ? "This is the active published version. To make changes, create a new draft version — old versions stay viewable for audit."
            : "This version is published or archived. Read-only for audit purposes."}
        </div>
      )}

      {preview ? (
        <div className="prose prose-sm max-w-none border border-[#e5e7eb] rounded-md p-5 bg-[#f9fafb]" data-testid="terms-preview-body">
          <MarkdownLite text={body} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_160px] gap-3">
            <Input
              value={title}
              disabled={!editable}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              className="bg-white border-[#e5e7eb] text-[14px] disabled:opacity-70"
              data-testid="terms-editor-title"
            />
            <Input
              value={version}
              disabled={!editable}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="Version (e.g., 1.0)"
              className="bg-white border-[#e5e7eb] text-[14px] disabled:opacity-70"
              data-testid="terms-editor-version"
            />
            <Input
              type="date"
              value={effectiveDate}
              disabled={!editable}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="bg-white border-[#e5e7eb] text-[14px] disabled:opacity-70"
              data-testid="terms-editor-effective-date"
            />
          </div>
          <Textarea
            value={body}
            disabled={!editable}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Paste full Terms & Conditions text (Markdown supported)…"
            className="bg-white border-[#e5e7eb] text-[13px] font-mono min-h-[480px] disabled:opacity-70"
            data-testid="terms-editor-body"
          />
          <Input
            value={notes}
            disabled={!editable}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes / change summary (not shown to users)"
            className="bg-white border-[#e5e7eb] text-[13px] disabled:opacity-70"
            data-testid="terms-editor-notes"
          />
          {editable && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-[11.5px] text-[#6b7280]">
                {dirty ? "Unsaved changes" : "Saved"}
              </div>
              <Button
                onClick={() => onSave(doc.id, { title, version, body, effective_date: effectiveDate, notes })}
                disabled={!dirty}
                className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white disabled:opacity-50"
                data-testid="terms-editor-save-btn"
              >
                <Save className="h-4 w-4 mr-1" /> Save Draft
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NewTermsModal = ({ productType, session, onClose, onCreated }) => {
  const [title, setTitle] = useState("MySchoolRx — Terms & Conditions");
  const [version, setVersion] = useState("");
  const [body, setBody] = useState("");
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!version.trim() || !body.trim()) {
      toast.error("Version and body are required");
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/api/admin/terms`, {
        product_type: productType,
        title,
        version,
        body,
        status: "draft",
      }, { headers: adminHdrs(session) });
      toast.success(`Draft v${version} created`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="terms-new-modal">
        <div className="px-6 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#111827]">New Terms Version</h2>
          <button onClick={onClose} className="text-[#6b7280] hover:text-[#111827] text-xl">×</button>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="bg-white border-[#e5e7eb]" data-testid="terms-new-title" />
            <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version (e.g., 1.1)" className="bg-white border-[#e5e7eb]" data-testid="terms-new-version" />
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Paste new Terms & Conditions body here (Markdown supported)…"
            className="bg-white border-[#e5e7eb] text-[13px] font-mono min-h-[400px]"
            data-testid="terms-new-body"
          />
          <div className="rounded-md border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2 text-[11.5px] text-[#1e3a8a]">
            New version starts as <strong>draft</strong>. You can preview, edit, and publish it from the version list. Publishing automatically archives the prior active version.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#e5e7eb] flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-[#e5e7eb]">Cancel</Button>
          <Button onClick={create} disabled={creating} className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white disabled:opacity-50" data-testid="terms-new-create-btn">
            {creating ? "Creating…" : "Create Draft"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AcceptancesDrawer = ({ acceptances, onClose }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end" onClick={onClose}>
    <div className="bg-white shadow-2xl w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="terms-acceptances-drawer">
      <div className="px-6 py-4 border-b border-[#e5e7eb] flex items-center justify-between sticky top-0 bg-white">
        <div>
          <h2 className="text-lg font-bold text-[#111827]">Acceptance Audit</h2>
          <p className="text-[11.5px] text-[#6b7280]">{acceptances.length} acceptance record{acceptances.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={onClose} className="text-[#6b7280] hover:text-[#111827] text-xl">×</button>
      </div>
      <div className="p-6 space-y-2">
        {acceptances.length === 0 && <p className="text-[#9ca3af] text-[13px]">No acceptances recorded for this version yet.</p>}
        {acceptances.map((a) => (
          <div key={a.id} className="rounded-md border border-[#e5e7eb] bg-[#f9fafb] p-3 text-[12px]">
            <div className="flex items-center justify-between gap-2">
              <strong className="text-[#111827]">{a.user_id}</strong>
              <span className="text-[11.5px] uppercase font-bold text-[#3B4FE4]">{a.acceptance_context}</span>
            </div>
            <div className="text-[#6b7280] mt-1">
              Tenant: {a.tenant_id || "—"} · {new Date(a.accepted_at).toLocaleString()}
            </div>
            {a.ip_address && <div className="text-[11.5px] text-[#9ca3af] mt-1">IP: {a.ip_address} · {a.user_agent?.substring(0, 80) || ""}</div>}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Lightweight markdown preview — handles headings, bold, lists. Good enough for Terms preview.
const MarkdownLite = ({ text }) => {
  const lines = (text || "").split("\n");
  const out = [];
  let ulBuf = [];
  const flushUl = () => {
    if (ulBuf.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 my-2 space-y-1">{ulBuf.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(b) }} />)}</ul>);
      ulBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
  lines.forEach((line, i) => {
    if (line.startsWith("# ")) { flushUl(); out.push(<h1 key={i} className="text-2xl font-bold text-[#111827] mt-4 mb-2">{line.slice(2)}</h1>); }
    else if (line.startsWith("## ")) { flushUl(); out.push(<h2 key={i} className="text-lg font-bold text-[#111827] mt-4 mb-2">{line.slice(3)}</h2>); }
    else if (line.startsWith("### ")) { flushUl(); out.push(<h3 key={i} className="text-base font-bold text-[#111827] mt-3 mb-1">{line.slice(4)}</h3>); }
    else if (line.startsWith("- ")) { ulBuf.push(line.slice(2)); }
    else if (line.trim() === "") { flushUl(); out.push(<div key={i} className="h-2" />); }
    else { flushUl(); out.push(<p key={i} className="text-[13px] text-[#374151] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  });
  flushUl();
  return <div>{out}</div>;
};

export default TermsAdminPage;
